import React, { useState, useRef, useEffect } from 'react';
import './styles.css';
import DefaultButton from '../../../components/Accessibility/DefaultButton';
import UnderlineButton from '../../../components/UnderlineButton';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { faCircleInfo, faGear } from '@fortawesome/pro-duotone-svg-icons';
import ModalFeedBack from '../../../components/ModalFeedBack';
import AtalhoModal from './AtalhoModal';
import FeatureCard from './FeatureCard';
import { ModalFeedBackProps } from '../../../interfaces/props/ModalFeedBackProps';
import { getHomeData, saveAtalhos } from '../../../services/Home/HomeService';
import LoadingApp from '../../../components/LoadingApp';
import ModalActionResponsive from '../../../components/ModalActionResponsive';
import { MenuItem } from '../../../interfaces/Home/MenuItem';
import { useHomeContext } from '../../../Store/contexts/HomeContext';
import { Typography, useMediaQuery } from '@mui/material';
import AddShortcutCard from './AddShortcutCard/AddShortcutCard';
import { sxTitle } from './sxProps';

interface ShortcutsPanelProps {
  onShortcutClick: (atalho: MenuItem) => void;
}

const ShortcutsPanel: React.FC<ShortcutsPanelProps> = ({ onShortcutClick }) => {
  const { atalhos, setAtalhos } = useHomeContext();
  const { calculateFontStyle } = useAccessibility()
  const [editable, setEditable] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const dragItemIndex = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [showModalAtalho, setShowModalAtalho] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<null | ModalFeedBackProps>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModalAction, setShowModalAction] = useState<boolean>(false)

  const [atalhosInternos, setAtalhosInternos] = useState<MenuItem[]>(atalhos);

  const [numColumns, setNumColumns] = useState(1);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 499px)");

  const calculateColumns = () => {
    if (gridContainerRef.current) {
      const gridStyles = window.getComputedStyle(gridContainerRef.current);
      const columns = gridStyles.getPropertyValue('grid-template-columns');
      const columnCount = columns.split(' ').length;
      setNumColumns(columnCount);
    }
  };

  useEffect(() => {
    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => {
      window.removeEventListener('resize', calculateColumns);
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (focusIndex === null || !editable) return;

    switch (event.key) {
      case ' ':
        event.preventDefault();
        if (focusIndex === atalhosInternos.length) {
          setShowModalAtalho(true);
        } else if (selectedIndex === null) {
          setSelectedIndex(focusIndex);
        } else {
          setSelectedIndex(null);
        }
        break;
      case 'Enter':
        if (focusIndex === atalhosInternos.length) {
          setShowModalAtalho(true);
        }
        break;
      case 'ArrowRight':
        if (selectedIndex !== null) {
          handleMoveItem(selectedIndex, Math.min(selectedIndex + 1, atalhosInternos.length - 1));
        } else {
          setFocusIndex((prev) => (prev !== null && prev < atalhosInternos.length ? prev + 1 : prev));
        }
        break;
      case 'ArrowLeft':
        if (selectedIndex !== null) {
          handleMoveItem(selectedIndex, Math.max(selectedIndex - 1, 0));
        } else {
          setFocusIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
        }
        break;
      case 'ArrowDown':
        if (selectedIndex !== null) {
          const nextIndex = selectedIndex + numColumns;
          if (nextIndex < atalhosInternos.length) handleMoveItem(selectedIndex, nextIndex);
        } else {
          setFocusIndex((prev) => {
            if (prev === null) return null;
            const nextFocusIndex = prev + numColumns;
            return nextFocusIndex < atalhosInternos.length ? nextFocusIndex : prev;
          });
        }
        break;
      case 'ArrowUp':
        if (selectedIndex !== null) {
          const prevIndex = selectedIndex - numColumns;
          if (prevIndex >= 0) handleMoveItem(selectedIndex, prevIndex);
        } else {
          setFocusIndex((prev) => {
            if (prev === null) return null;
            const prevFocusIndex = prev - numColumns;
            return prevFocusIndex >= 0 ? prevFocusIndex : prev;
          });
        }
        break;
      default:
        break;
    }
  };


  const handleDragStart = (index: number) => {
    if (!editable) return;
    dragItemIndex.current = index;
    setIsDragging(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    event.preventDefault();
    if (editable && dragItemIndex.current !== null && dragItemIndex.current !== index) {
      handleMoveItem(dragItemIndex.current, index);
      dragItemIndex.current = index;
    }
  };

  const handleDrop = () => {
    dragItemIndex.current = null;
    resetStates()
  };

  const handleMoveItem = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const updatedAtalhos = [...atalhosInternos];
    const [movedItem] = updatedAtalhos.splice(fromIndex, 1);
    updatedAtalhos.splice(toIndex, 0, movedItem);

    setAtalhosInternos(updatedAtalhos);
    setSelectedIndex(toIndex);
    setFocusIndex(toIndex);
  };

  const handleFocus = (index: number) => {
    setFocusIndex(index);
  };

  const handleCardClick = (index: number) => {
    if (selectedIndex === null) {
      setSelectedIndex(index);
    } else {
      handleMoveItem(selectedIndex, index);
      setSelectedIndex(null);
    }
  };

  const showFeedbackModal = (config: ModalFeedBackProps) => {
    setModalFeedback({
      ...config,
      handleShowModal: config.handleShowModal || (() => setModalFeedback(null)),
      handleButtonModal: config.handleButtonModal || (() => {
        setModalFeedback(null);
      }),
    });
  };

  const handleShowModalInfo = () => {
    showFeedbackModal({
      icon: "info",
      title: "Como personalizar meus atalhos?",
      subTitle: "Teclado: Para navegar entre as seções de ajuda, atalhos (reordenar e adicionar) e botões de Salvar ou Cancelar, use a tecla Tab do seu teclado. Use as setas e a tecla Espaço para selecionar e reordenar os atalhos em sua tela.<br/> Mouse: Para reordenar os atalhos, clique e arraste para o local desejado ou clique no item e depois clique na posição para qual deseja movimentá-lo.",
      textButton: "Voltar",
      showButtonModal: true,
      showModal: true,
      classNameModal: 'modal-info'
    });
  }

  const handleSelectAtalho = (item: MenuItem) => {
    if (!atalhosInternos.some((atalho) => atalho.title === item.title)) {
      setAtalhosInternos((prev) => [...prev, item]);
    }
    setShowModalAtalho(false);
  };

  const handleRemoveAtalho = (index: number) => {
    if (atalhosInternos.length > 1) {
      const updatedAtalhos = atalhosInternos.filter((_, i) => i !== index);
      setAtalhosInternos(updatedAtalhos);
    } else {
      showFeedbackModal({
        icon: "warning",
        title: "Atenção",
        subTitle: "Você deve ter no mínimo um (01) atalho em sua tela inicial. Por favor, adicione um atalho.",
        textButton: "Voltar",
        showButtonModal: true,
        showModal: true
      });
    }
  };

  const handleGetAtalhos = () => {
    setLoading(true)
    getHomeData().then((response) => {
      setAtalhos(response.atalhos)
    }).finally(() => {
      setLoading(false)
    })
  }

  const handleSaveAtalhos = () => {
    saveAtalhos(atalhosInternos).then(() => {
      showFeedbackModal({
        icon: "success",
        title: "Atalhos salvos com sucesso!",
        textButton: "Fechar",
        showButtonModal: true,
        showModal: true
      });
      setEditable(false)
      handleGetAtalhos()
    }).catch(() => {
      showFeedbackModal({
        icon: "error",
        title: "Atenção",
        subTitle: "Ocorreu um erro ao salvar os atalhos. Tente novamente em instantes.",
        textButton: "Voltar",
        showButtonModal: true,
        showModal: true
      });
    })
  }

  const resetStates = () => {
    setSelectedIndex(null);
    setFocusIndex(null);
    setIsDragging(false);
  };

  const handleCancel = () => {
    resetStates();
    setAtalhosInternos(atalhos)
    setEditable(false);
    setShowModalAction(false)
  }

  const handleEditClick = () => {
    setEditable(true);

    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  return (
    <div className='page-container'>
      <LoadingApp showLoading={loading} />
      <div className={`container-header ${editable ? 'no-space' : ''}`}>
        <Typography
          component="h1"
          style={calculateFontStyle(isMobile ? 20 : 32)}
          sx={sxTitle}>
          Meus atalhos
        </Typography>
        { editable ? <div ref={buttonRef} tabIndex={-1}>
          <UnderlineButton onClick={handleShowModalInfo} icon={faCircleInfo}>
            Como personalizar meus atalhos?
          </UnderlineButton>
        </div> : <UnderlineButton onClick={handleEditClick} icon={faGear}>
          Editar atalhos
        </UnderlineButton> }
      </div>
      <div
        className="container-shortcuts-painel"
        onKeyDown={handleKeyDown}
        aria-label="Painel de atalhos"
        role="list"
        ref={gridContainerRef}
      >
        {atalhosInternos.map((atalho, index) => (
          <div
            key={atalho.component}
            onClick={() => { editable && handleCardClick(index) }}
            className={`shortcut-item 
              ${focusIndex === index ? 'focused' : ''} 
              ${selectedIndex === index ? 'selected' : ''} 
              ${isDragging || selectedIndex !== null ? (focusIndex !== index ? 'dimmed' : '') : ''}`}
            data-index={index}
            tabIndex={-1}
            onFocus={() => handleFocus(index)}
            draggable={editable}
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={handleDrop}
          >
            <FeatureCard
              feature={atalho?.title}
              icon={atalho.icon}
              restricted={atalho.restricted}
              component={atalho.component}
              to={() => {
                !editable && onShortcutClick(atalho);
              }}
              iconDimensionClass="fa-icon32x32"
              remove={editable}
              onRemove={() => handleRemoveAtalho(index)}
              ariaLabel={`item ${index+1} de ${atalhosInternos.length}`}
            />
          </div>
        ))}
        {editable &&
          <AddShortcutCard
            onClick={(e) => {
              e.stopPropagation();
              if (selectedIndex !== null) {
                setSelectedIndex(null);
              }
              setShowModalAtalho(true);
            }}
            onFocus={() => setFocusIndex(atalhosInternos.length)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowModalAtalho(true);
              }
            }}
          />
        }
      </div>
      {editable &&
        <div className='container-home-buttons'>
          <DefaultButton
            text='Salvar'
            variant="contained"
            onClick={handleSaveAtalhos} />
          <DefaultButton
            text='Cancelar'
            variant="outlined"
            onClick={() =>
              setShowModalAction(true)} />
        </div>}
      {modalFeedback && (
        <ModalFeedBack
          {...modalFeedback}
        />
      )}
      <AtalhoModal
        open={showModalAtalho}
        atalhosSelecionados={atalhosInternos}
        onSelect={handleSelectAtalho}
        onClose={() => setShowModalAtalho(false)}
      />
      <ModalActionResponsive
        showModal={showModalAction}
        textContainedButton='Cancelar'
        textOutlinedButton='Voltar'
        title='Atenção'
        colorContainedButton='error'
        subTitle='Você irá perder todas as alterações feitas. Deseja cancelar mesmo assim?'
        icon="warning"
        handleContainedButton={handleCancel}
        handleShowModal={() => setShowModalAction(false)}
        handleOutlinedButton={() => setShowModalAction(false)}
      />
    </div>
  );
};

export default ShortcutsPanel;
